import { useEffect, useState } from "react";
import { usePumpSizing } from "../../../context/PumpSizingContext";
import { SelectField } from "../../shared/SelectField";
import { SwitchField } from "../../shared/SwitchField";
import { SERIES_TYPE_LABELS } from "../../../data/chart";
import {
	emptySeriesFilters,
	SERIES_FILTER_FIELDS,
} from "../../../data/product";
import { ChartSeries, ProductDataRow } from "../../../types/types";
import { getSeriesType } from "../../../utils/helpers/chartHelpers";
import { formatNumericValue } from "../../../utils/helpers/helpers";
import {
	filterProducts,
	formatProductFieldValue,
	normalizeSeriesKey,
	normalizeSeriesLabel,
	productsForSeries,
	uniqueFilterOptions,
	uniqueJoinedValues,
} from "../../../utils/helpers/productHelpers";

type SeriesSelectorProps = {
	series: ChartSeries[];
};

function SeriesProductTable({
	rows,
	seriesLabel,
}: {
	rows: ProductDataRow[];
	seriesLabel: string;
}) {
	if (rows.length === 0) {
		return null;
	}

	return (
		<table className="pf-series-table">
			<caption className="pf-sr-only">{seriesLabel} models</caption>
			<thead>
				<tr>
					<th scope="col">Model</th>
					<th scope="col">HP</th>
					<th scope="col">Voltage</th>
					<th scope="col">Phase</th>
					<th scope="col">Full Load Amps</th>
					<th scope="col">Cord Length</th>
					<th scope="col">Discharge</th>
					<th scope="col">Solids Handling</th>
					<th scope="col">Switch Type</th>
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.partNum}>
						<td>{row.partNum || "—"}</td>
						<td>{row.hp || "—"}</td>
						<td>{row.volts || "—"}</td>
						<td>{row.phase || "—"}</td>
						<td>{row.amps || "—"}</td>
						<td>
							{formatProductFieldValue(
								"cordLengthInFeet",
								row.cordLengthInFeet,
							) || "—"}
						</td>
						<td>
							{formatProductFieldValue(
								"dischargeSizeInInches",
								row.dischargeSizeInInches,
							) || "—"}
						</td>
						<td>
							{formatProductFieldValue(
								"solidsHandlingInInches",
								row.solidsHandlingInInches,
							) || "—"}
						</td>
						<td>{row.floatType || "—"}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

export function SeriesSelector({ series }: SeriesSelectorProps) {
	const {
		formData,
		productData: { products, seriesUrls, seriesImages },
		hiddenSeries,
		seriesFilters,
		toggleSeriesCurve,
		setSeriesFilter,
	} = usePumpSizing();
	// Only the sewage/grinder application type mixes two pump kinds in one list.
	const showSeriesTypeBadge = formData.applicationType === "sewage";

	const [expandedSeries, setExpandedSeries] = useState<string | null>(null);

	// Filters live in the context so they survive collapsing and are saved with
	// the submission; only the expanded card is reset here.
	useEffect(() => {
		setExpandedSeries(null);
	}, [series]);

	if (series.length === 0) {
		return null;
	}

	const handleToggle = (seriesCode: string) => {
		setExpandedSeries((previous) =>
			previous === seriesCode ? null : seriesCode,
		);
	};

	return (
		<section className="pf-series-selector" aria-label="Pump series results">
			{products.length > 0 ? (
				<ul className="pf-series-list">
					{series.map((item) => {
						const seriesProducts = productsForSeries(
							products,
							item.series_code,
						);
						const hp = uniqueJoinedValues(seriesProducts, "hp");
						const discharge = uniqueJoinedValues(
							seriesProducts,
							"dischargeSizeInInches",
							" - ",
							(v) => `${v}"`,
						);
						const seriesLabel = item.display_value;
						const filters =
							seriesFilters[item.series_code] ?? emptySeriesFilters();
						const filteredRows = filterProducts(seriesProducts, filters);
						const seriesId = normalizeSeriesLabel(seriesLabel);
						const isExpanded = expandedSeries === seriesId;
						const isCurveVisible = !hiddenSeries.has(seriesId);
						const gpmLabel = formatNumericValue(item.gpm);
						const tdhLabel = formatNumericValue(item.tdh);
						const productUrl =
							seriesUrls[normalizeSeriesKey(item.series_code)] ?? "";
						const productImage =
							seriesImages[normalizeSeriesKey(item.series_code)] ?? "";

						const detailsId = `series-details-${seriesId}`;

						return (
							<li
								key={seriesId}
								data-series-code={item.series_code}
								className={`pf-series-card${isExpanded ? " pf-series-card--expanded" : ""}`}
							>
								{/* The whole row folds the card open; the chevron carries the
								    ARIA state so keyboard users get the same control. */}
								<div
									className="pf-series-card__header"
									onClick={() => handleToggle(seriesId)}
								>
									{productImage ? (
										<img
											className="pf-series-card__image"
											src={productImage}
											alt=""
											loading="lazy"
										/>
									) : (
										<span className="pf-series-card__image pf-series-card__image--empty" />
									)}

									<div className="pf-series-card__main">
										<div className="pf-series-card__heading">
											{productUrl ? (
												<a
													className="pf-series-card__name"
													href={productUrl}
													target="_blank"
													rel="noopener noreferrer"
													onClick={(event) => event.stopPropagation()}
												>
													{seriesLabel}
												</a>
											) : (
												<span className="pf-series-card__name">
													{seriesLabel}
												</span>
											)}
											{showSeriesTypeBadge ? (
												<span className="pf-series-card__badge">
													{SERIES_TYPE_LABELS[getSeriesType(item)]}
												</span>
											) : null}
										</div>
										<div className="pf-series-card__specs">
											<span>{hp ? `${hp} hp` : "—"}</span>
											<span>{discharge ? `${discharge} Discharge` : "—"}</span>
										</div>
									</div>

									<div className="pf-series-card__metrics">
										<span>{gpmLabel ? `${gpmLabel} GPM` : ""}</span>
										<span>{tdhLabel ? `${tdhLabel} TDH` : ""}</span>
									</div>

									<div className="pf-series-card__actions">
										{/* Flipping the curve switch must not fold the card. */}
										<div
											className="pf-series-card__curve"
											onClick={(event) => event.stopPropagation()}
										>
											<SwitchField
												id={`series-curve-${seriesId}`}
												label="Display Series"
												checked={isCurveVisible}
												onChange={() => toggleSeriesCurve(seriesId)}
											/>
										</div>

										{/* Deliberately handler-free: the click bubbles to the header,
										    so adding one here would toggle twice. */}
										<div className="pf-series-card__models">
											<span className="pf-series-card__models-label">
												Select Models
											</span>
											<button
												type="button"
												className="pf-series-card__chevron"
												aria-expanded={isExpanded}
												aria-controls={detailsId}
												aria-label={`Select Models, ${isExpanded ? "hide" : "show"} ${seriesLabel} models`}
											>
												<svg
													viewBox="0 0 16 16"
													aria-hidden="true"
													focusable="false"
												>
													<path
														d="M4 6.5 8 10.5 12 6.5"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</button>
										</div>
									</div>
								</div>

								{/* Rendered even while collapsed so the panel can animate
								    open and closed from CSS. */}
								<div className="pf-series-card__details" id={detailsId}>
									<div className="pf-series-card__details-inner">
										<div className="pf-series-filters">
											{SERIES_FILTER_FIELDS.map((field) => {
												const options = uniqueFilterOptions(
													seriesProducts,
													field.key,
												).map((value) => ({
													value,
													label: formatProductFieldValue(field.key, value),
												}));

												if (options.length === 0) {
													return null;
												}

												const isSingleOption = options.length === 1;
												const currentValue = filters[field.key];
												const effectiveValue =
													isSingleOption ? options[0].value : currentValue;

												return (
													<SelectField
														key={field.key}
														label={field.label}
														options={options}
														value={effectiveValue}
														disabled={isSingleOption}
														onChange={(event) =>
															setSeriesFilter(
																item.series_code,
																field.key,
																event.target.value,
															)
														}
														placeholder={`All ${field.label}`}
													/>
												);
											})}
										</div>

										{filteredRows.length === 0 ? (
											<p className="pf-series-empty">
												No models match these filters. Contact Liberty Pumps
												for help selecting a pump.
											</p>
										) : (
											<div
												className="pf-series-table-wrap"
												tabIndex={0}
												role="region"
												aria-label={`${seriesLabel} models`}
											>
												<SeriesProductTable
													rows={filteredRows}
													seriesLabel={seriesLabel}
												/>
											</div>
										)}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			) : null}

			{products.length > 0 ? (
				<div className="pf-series-pdf-tables" hidden>
					{series.map((item) => {
						const seriesProducts = productsForSeries(
							products,
							item.series_code,
						);
						const seriesLabel = item.display_value;
						const filteredSeriesProducts = filterProducts(
							seriesProducts,
							seriesFilters[item.series_code] ?? emptySeriesFilters(),
						);

						if (filteredSeriesProducts.length === 0) {
							return null;
						}

						return (
							<div
								key={`pdf-${normalizeSeriesLabel(seriesLabel)}`}
								data-series-code={item.series_code}
							>
								<SeriesProductTable
									rows={filteredSeriesProducts}
									seriesLabel={seriesLabel}
								/>
							</div>
						);
					})}
				</div>
			) : null}
		</section>
	);
}
